import { configure, setAuthStorage, awsConfig, isConfigured } from '@ogaim/core';
import { setSoundPlayer, type SoundPlayer } from './sounds';
import {
  DEFAULT_THEME_ID,
  THEME_STORAGE_KEY,
  WALLPAPER_STORAGE_KEY,
  WALLPAPER_CUSTOM_KEY,
  type ThemeId,
  type WallpaperId,
} from '@ogaim/ui';

export interface BootstrapOptions {
  /**
   * Where Cognito keeps its tokens. Pass AsyncStorage (or any store with the
   * same four methods) — React Native has no localStorage.
   */
  storage: {
    setItem(key: string, value: string): void;
    getItem(key: string): string | null;
    removeItem(key: string): void;
    clear(): void;
  };
  soundPlayer?: SoundPlayer;
}

/**
 * The theme chosen on this device, read synchronously from the same storage
 * the auth tokens use. Read at startup so the first paint is already correct
 * rather than flashing the default and re-rendering.
 */
export function loadThemeId(storage: BootstrapOptions['storage']): ThemeId {
  try {
    const saved = storage.getItem(THEME_STORAGE_KEY);
    return (saved as ThemeId) ?? DEFAULT_THEME_ID;
  } catch {
    return DEFAULT_THEME_ID;
  }
}

export function saveThemeId(storage: BootstrapOptions['storage'], id: ThemeId): void {
  try {
    storage.setItem(THEME_STORAGE_KEY, id);
  } catch {
    // A theme that fails to persist is a cosmetic loss, never an error.
  }
}

export interface WallpaperChoice {
  id: WallpaperId;
  customUri: string | null;
}

export function loadWallpaper(storage: BootstrapOptions['storage']): WallpaperChoice {
  try {
    return {
      id: (storage.getItem(WALLPAPER_STORAGE_KEY) as WallpaperId) ?? 'none',
      customUri: storage.getItem(WALLPAPER_CUSTOM_KEY),
    };
  } catch {
    return { id: 'none', customUri: null };
  }
}

export function saveWallpaper(
  storage: BootstrapOptions['storage'],
  id: WallpaperId,
  customUri?: string | null
): void {
  try {
    storage.setItem(WALLPAPER_STORAGE_KEY, id);
    if (customUri) storage.setItem(WALLPAPER_CUSTOM_KEY, customUri);
  } catch {
    /* cosmetic */
  }
}

/**
 * Wires the platform-specific bits into the shared core. Call once, before
 * rendering <App />.
 */
export function bootstrap({ storage, soundPlayer }: BootstrapOptions): void {
  if (!isConfigured) {
    console.warn(
      '[og-aim] No backend configured. Run `pnpm infra:deploy` to generate ' +
        'packages/core/src/aws-config.generated.ts before signing on.'
    );
  }
  configure(awsConfig);
  setAuthStorage(storage);
  if (soundPlayer) setSoundPlayer(soundPlayer);
}
